import { getBackRouteIndex } from '@/utils/routes';
import { logger } from '@rhg/libs/logger';
import { useRouter } from 'next/router';
import { PropsWithChildren, createContext, useEffect, useState } from 'react';

export interface HistoryContextProps {
    history: string[];
    setHistory: (history: string[]) => void;
    back: (fallbackRoute?: string) => void;
}
export const HistoryContext = createContext({} as HistoryContextProps);

export const HistoryProvider = ({ children }: PropsWithChildren) => {
    const { asPath, push, isReady } = useRouter();
    const [history, setHistory] = useState<string[]>([]);

    useEffect(() => {
        if (isReady) {
            setHistory((prevHistory) => [...prevHistory, asPath]);
        }
    }, [asPath, isReady]);

    const back = (fallbackRoute?: string) => {
        // The last entry is the current page.
        const validRouteIndex = getBackRouteIndex(
            history.slice(0, -1),
            (entry) => entry === asPath
        );

        let targetRoute = fallbackRoute || '/';
        if (validRouteIndex >= 0) {
            targetRoute = history[validRouteIndex];
            // Navigate to the valid route and update history
            // Slice to validRouteIndex (not +1) to avoid duplication when useEffect runs after navigation
            const newHistory = history.slice(0, validRouteIndex);
            setHistory(newHistory);
        } else {
            setHistory([]);
        }

        push(targetRoute).catch((err) =>
            logger.error(
                'Failed to navigate back in HistoryProvider',
                { targetRoute },
                err as Error
            )
        );
    };

    return (
        <HistoryContext.Provider
            value={{
                back,
                history,
                setHistory,
            }}
        >
            {children}
        </HistoryContext.Provider>
    );
};
